import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Quote, Star, TrendingUp, ArrowRight, BadgeCheck, ShieldCheck, Headphones, Award } from 'lucide-react';

const TESTIMONIALS = [
  {
    name: 'Aarav Mehta',
    role: 'Software Engineer · Bangalore',
    avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&q=80',
    text: 'Bharat AI suggested the perfect 2BHK in Koramangala for me. Zero brokerage, RERA-verified — and the agent walked me through the registration too. Couldn’t recommend more.',
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    role: 'Doctor · Mumbai',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
    text: 'I was looking for a sea-view flat in Worli for months. The premium concierge service shortlisted three options in 48 hours. The buying process was effortless.',
    rating: 5,
  },
  {
    name: 'Rohan Iyer',
    role: 'Founder · Goa',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80',
    text: 'Bought a heritage Portuguese villa in Anjuna through Bharat Estate. The legal team handled stamp duty, registration and even helped with renovations.',
    rating: 5,
  },
];

const TRENDING_CITIES = [
  { city: 'Mumbai', tagline: 'Sea-views & city pulse', image: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=900&q=80', delta: '+8.2%' },
  { city: 'Bangalore', tagline: 'Tech-park homes', image: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=900&q=80', delta: '+11.4%' },
  { city: 'Delhi', tagline: 'Heritage bungalows', image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=900&q=80', delta: '+5.6%' },
  { city: 'Pune', tagline: 'Family-friendly suburbs', image: 'https://images.unsplash.com/photo-1593696140826-c58b021acf8b?w=900&q=80', delta: '+9.1%' },
  { city: 'Hyderabad', tagline: 'Skyline at HITEC city', image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=900&q=80', delta: '+12.7%' },
  { city: 'Goa', tagline: 'Beachside villas', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=900&q=80', delta: '+14.3%' },
];

export const TrustStrip = () => (
  <section className="bg-white border-y border-black/5" data-testid="trust-strip">
    <div className="container-luxury py-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { icon: BadgeCheck, label: 'RERA Verified', sub: 'Every listing screened' },
          { icon: ShieldCheck, label: '0% Brokerage', sub: 'No hidden fees, ever' },
          { icon: Headphones, label: '24×7 Concierge', sub: 'India + NRI desk' },
          { icon: Award, label: '24+ Cities', sub: 'Across India' },
        ].map((t) => (
          <div key={t.label} className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-[#0F172A] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <t.icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[#0F172A] font-semibold text-sm md:text-base leading-tight">{t.label}</p>
              <p className="text-muted-foreground text-xs">{t.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const TrendingCities = () => (
  <section className="section-gap bg-[#F9F9F7]" data-testid="trending-cities-section">
    <div className="container-luxury">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="caption mb-3">Where India is moving</p>
          <h2 className="heading-secondary text-[#0F172A]">Trending Cities</h2>
        </div>
        <Link to="/market-trends" className="flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors font-medium">
          See full market report <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {TRENDING_CITIES.map((c, idx) => (
          <motion.div
            key={c.city}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: idx * 0.05 }}
          >
            <Link
              to={`/real-estate/${c.city.toLowerCase()}`}
              className="group block relative aspect-[4/5] md:aspect-[5/6] rounded-2xl overflow-hidden shadow-lg"
              data-testid={`trending-city-${c.city.toLowerCase()}`}
            >
              <img
                src={c.image}
                alt={c.city}
                loading="lazy"
                onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1502672023488-70e25813eb80?w=900&q=80'; }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute top-3 right-3 bg-[#D4AF37] text-[#0F172A] text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
                <TrendingUp className="h-3 w-3" /> {c.delta}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <p className="font-['Plus_Jakarta_Sans'] text-white text-2xl md:text-3xl font-semibold leading-none">
                  {c.city}
                </p>
                <p className="text-white/75 text-xs md:text-sm mt-1">{c.tagline}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const Testimonials = () => (
  <section className="section-gap bg-white" data-testid="testimonials-section">
    <div className="container-luxury">
      <div className="text-center mb-12">
        <p className="caption mb-3">Voices of Bharat</p>
        <h2 className="heading-secondary text-[#0F172A]">Loved by 10,000+ Families</h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="relative bg-[#F9F9F7] rounded-2xl p-6 md:p-7 border border-black/5 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all"
          >
            <Quote className="absolute -top-3 -left-2 h-9 w-9 text-[#D4AF37]/20 fill-[#D4AF37]/10" />
            <div className="flex items-center gap-1 text-[#D4AF37] mb-4">
              {Array.from({ length: t.rating }).map((_, idx) => (
                <Star key={idx} className="h-4 w-4 fill-[#D4AF37]" />
              ))}
            </div>
            <p className="text-[#0F172A] text-sm md:text-[15px] leading-relaxed mb-6">
              “{t.text}”
            </p>
            <div className="flex items-center gap-3 pt-4 border-t border-black/5">
              <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-[#0F172A] text-sm">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.role}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
